/**
 * AutomationRulesPage — Configure trigger → action automation rules.
 *
 * Features:
 *   - List all automation rules with status toggles
 *   - Create/edit rules with trigger event, action type, delay, conditions
 *   - Execution history per rule
 *   - Pre-built rule templates for common scenarios
 *
 * @module packages/billing-admin/src/pages/AutomationRulesPage
 * @since 2026-06-20
 */
import React, { useState, useMemo } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useOrgContext } from '../contexts/OrgContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AutomationRule {
  id: string;
  name: string;
  description: string | null;
  triggerEvent: string;
  actionType: string;
  delayDays: number;
  conditions: Record<string, unknown>;
  actionConfig: Record<string, unknown>;
  isActive: boolean;
  priority: number;
  createdAt: string;
  updatedAt: string;
}

interface RuleExecution {
  id: string;
  ruleId: string;
  triggerEvent: string;
  customerId: string | null;
  status: string;
  scheduledAt: string;
  executedAt: string | null;
  result: Record<string, unknown>;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TRIGGER_EVENTS = [
  { value: 'subscription.created', label: 'Subscription Created', icon: '🟢' },
  { value: 'subscription.canceled', label: 'Subscription Cancelled', icon: '🔴' },
  { value: 'subscription.expired', label: 'Subscription Expired', icon: '⚫' },
  { value: 'subscription.trial_ending', label: 'Trial Ending', icon: '⏳' },
  { value: 'subscription.upgraded', label: 'Subscription Upgraded', icon: '⬆️' },
  { value: 'subscription.downgraded', label: 'Subscription Downgraded', icon: '⬇️' },
  { value: 'invoice.past_due', label: 'Invoice Past Due', icon: '⚠️' },
  { value: 'payment.failed', label: 'Payment Failed', icon: '❌' },
  { value: 'payment.succeeded', label: 'Payment Succeeded', icon: '✅' },
  { value: 'customer.reinstated', label: 'Customer Reinstated', icon: '🔄' },
  { value: 'customer.suspended', label: 'Customer Suspended', icon: '🚫' },
  { value: 'entitlements.granted', label: 'Entitlements Granted', icon: '🔓' },
  { value: 'entitlements.revoked', label: 'Entitlements Revoked', icon: '🔒' },
];

const ACTION_TYPES = [
  { value: 'send_webhook', label: 'Send Webhook', icon: '🔗', description: 'POST event data to an external URL' },
  { value: 'send_email', label: 'Send Email', icon: '📧', description: 'Send a template-based email to the customer' },
  { value: 'grant_entitlements', label: 'Grant Entitlements', icon: '🔓', description: 'Provision access (roles/features)' },
  { value: 'revoke_entitlements', label: 'Revoke Entitlements', icon: '🔒', description: 'Deprovision access (roles/features)' },
  { value: 'restrict_access', label: 'Restrict Access', icon: '⚡', description: 'Downgrade to a limited access tier' },
  { value: 'notify_external', label: 'Notify External', icon: '📡', description: 'Send notification to Slack, PagerDuty, etc.' },
];

const RULE_TEMPLATES = [
  {
    name: 'Payment Reminder (7 days)',
    description: 'Send email reminder when invoice is past due for 7 days',
    triggerEvent: 'invoice.past_due',
    actionType: 'send_email',
    delayDays: 7,
    actionConfig: { templateSlug: 'payment-reminder' },
  },
  {
    name: 'Suspend Access (14 days unpaid)',
    description: 'Revoke entitlements after 14 days past due',
    triggerEvent: 'invoice.past_due',
    actionType: 'revoke_entitlements',
    delayDays: 14,
    actionConfig: {},
  },
  {
    name: 'Provision on Subscription',
    description: 'Grant entitlements when a new subscription is created',
    triggerEvent: 'subscription.created',
    actionType: 'grant_entitlements',
    delayDays: 0,
    actionConfig: {},
  },
  {
    name: 'Deprovision on Cancel',
    description: 'Revoke entitlements when subscription is cancelled',
    triggerEvent: 'subscription.canceled',
    actionType: 'revoke_entitlements',
    delayDays: 0,
    actionConfig: {},
  },
  {
    name: 'Webhook on Payment Failure',
    description: 'Send webhook to external system when payment fails',
    triggerEvent: 'payment.failed',
    actionType: 'send_webhook',
    delayDays: 0,
    actionConfig: { url: '', method: 'POST' },
  },
  {
    name: 'Restore Access on Reinstatement',
    description: 'Re-grant entitlements when customer is reinstated',
    triggerEvent: 'customer.reinstated',
    actionType: 'grant_entitlements',
    delayDays: 0,
    actionConfig: {},
  },
];

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useApiBase(): string {
  const { apiBaseUrl } = useAdminAuth();
  const { activeOrg } = useOrgContext();
  return `${apiBaseUrl}/api/v1/orgs/${activeOrg?.orgId ?? 'default'}`;
}

function useAuthHeaders(): Record<string, string> {
  const { token } = useAdminAuth();
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function useRules() {
  const base = useApiBase();
  const headers = useAuthHeaders();
  return useQuery<AutomationRule[]>({
    queryKey: ['automation-rules'],
    queryFn: async () => {
      const res = await fetch(`${base}/automation-rules`, { headers });
      if (!res.ok) throw new Error('Failed to load rules');
      const data = await res.json();
      return data.rules;
    },
  });
}

function useRuleExecutions(ruleId: string | null) {
  const base = useApiBase();
  const headers = useAuthHeaders();
  return useQuery<RuleExecution[]>({
    queryKey: ['automation-rule-executions', ruleId],
    queryFn: async () => {
      if (!ruleId) return [];
      const res = await fetch(`${base}/automation-rules/${ruleId}/executions`, { headers });
      if (!res.ok) throw new Error('Failed to load executions');
      const data = await res.json();
      return data.executions;
    },
    enabled: !!ruleId,
  });
}

interface PlanSummary { id: string; name: string; slug: string; status: string }

function usePlansList() {
  const { apiBaseUrl } = useAdminAuth();
  const headers = useAuthHeaders();
  return useQuery<PlanSummary[]>({
    queryKey: ['admin', 'plans', 'list'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/admin/plans`, { headers });
      if (!res.ok) throw new Error('Failed to load plans');
      return res.json();
    },
  });
}

function useRulePlanMappings() {
  const base = useApiBase();
  const headers = useAuthHeaders();
  return useQuery<Record<string, string[]>>({
    queryKey: ['automation-rules-plan-mappings'],
    queryFn: async () => {
      const res = await fetch(`${base}/automation-rules/plans-map/all`, { headers });
      if (!res.ok) throw new Error('Failed to load plan mappings');
      const data = await res.json();
      return data.mappings;
    },
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type EditorMode = 'list' | 'create' | 'edit' | 'history';

export default function AutomationRulesPage(): React.JSX.Element {
  const base = useApiBase();
  const headers = useAuthHeaders();
  const qc = useQueryClient();

  const { data: rules = [], isLoading } = useRules();
  const { data: allPlans = [] } = usePlansList();
  const { data: rulePlanMappings = {} } = useRulePlanMappings();
  const [mode, setMode] = useState<EditorMode>('list');
  const [editingRule, setEditingRule] = useState<AutomationRule | null>(null);
  const [historyRuleId, setHistoryRuleId] = useState<string | null>(null);
  const { data: executions = [] } = useRuleExecutions(historyRuleId);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formTrigger, setFormTrigger] = useState('');
  const [formAction, setFormAction] = useState('');
  const [formDelay, setFormDelay] = useState(0);
  const [formActive, setFormActive] = useState(true);
  const [formPriority, setFormPriority] = useState(0);
  const [formWebhookUrl, setFormWebhookUrl] = useState('');
  const [formEmailTemplate, setFormEmailTemplate] = useState('');
  const [formPlanIds, setFormPlanIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Mutations
  const toggleMut = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const res = await fetch(`${base}/automation-rules/${id}/toggle`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ isActive }),
      });
      if (!res.ok) throw new Error('Toggle failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automation-rules'] }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${base}/automation-rules/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Delete failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automation-rules'] }),
  });

  // Helpers
  function resetForm(rule?: AutomationRule) {
    setFormName(rule?.name ?? '');
    setFormDescription(rule?.description ?? '');
    setFormTrigger(rule?.triggerEvent ?? '');
    setFormAction(rule?.actionType ?? '');
    setFormDelay(rule?.delayDays ?? 0);
    setFormActive(rule?.isActive ?? true);
    setFormPriority(rule?.priority ?? 0);
    setFormWebhookUrl((rule?.actionConfig as any)?.url ?? '');
    setFormEmailTemplate((rule?.actionConfig as any)?.templateSlug ?? '');
    setFormPlanIds(rule ? (rulePlanMappings[rule.id] ?? []) : []);
    setError('');
  }

  function openCreate(template?: typeof RULE_TEMPLATES[number]) {
    setEditingRule(null);
    resetForm();
    if (template) {
      setFormName(template.name);
      setFormDescription(template.description);
      setFormTrigger(template.triggerEvent);
      setFormAction(template.actionType);
      setFormDelay(template.delayDays);
      setFormWebhookUrl((template.actionConfig as any)?.url ?? '');
      setFormEmailTemplate((template.actionConfig as any)?.templateSlug ?? '');
    }
    setMode('create');
  }

  function openEdit(rule: AutomationRule) {
    setEditingRule(rule);
    resetForm(rule);
    setMode('edit');
  }

  function openHistory(ruleId: string) {
    setHistoryRuleId(ruleId);
    setMode('history');
  }

  async function handleSave() {
    if (!formName || !formTrigger || !formAction) {
      setError('Name, trigger, and action are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const actionConfig: Record<string, unknown> = {};
      if (formAction === 'send_webhook' && formWebhookUrl) actionConfig.url = formWebhookUrl;
      if (formAction === 'send_email' && formEmailTemplate) actionConfig.templateSlug = formEmailTemplate;

      const body = {
        name: formName,
        description: formDescription || undefined,
        triggerEvent: formTrigger,
        actionType: formAction,
        delayDays: formDelay,
        conditions: {},
        actionConfig,
        isActive: formActive,
        priority: formPriority,
      };

      const url = editingRule ? `${base}/automation-rules/${editingRule.id}` : `${base}/automation-rules`;
      const method = editingRule ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Save failed');
      }
      const { rule } = await res.json();
      const ruleId = editingRule?.id ?? rule?.id;

      // Save plan associations
      if (ruleId) {
        await fetch(`${base}/automation-rules/${ruleId}/plans`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ planIds: formPlanIds }),
        });
      }

      qc.invalidateQueries({ queryKey: ['automation-rules'] });
      qc.invalidateQueries({ queryKey: ['automation-rules-plan-mappings'] });
      setMode('list');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  // Derived
  const triggerLabel = (v: string) => TRIGGER_EVENTS.find(t => t.value === v)?.label ?? v;
  const triggerIcon = (v: string) => TRIGGER_EVENTS.find(t => t.value === v)?.icon ?? '⚡';
  const actionLabel = (v: string) => ACTION_TYPES.find(a => a.value === v)?.label ?? v;
  const actionIcon = (v: string) => ACTION_TYPES.find(a => a.value === v)?.icon ?? '⚙️';
  const planName = (id: string) => allPlans.find(p => p.id === id)?.name ?? id.slice(0, 8) + '…';

  const activeRules = useMemo(() => rules.filter(r => r.isActive), [rules]);
  const inactiveRules = useMemo(() => rules.filter(r => !r.isActive), [rules]);

  // -----------------------------------------------------------------------
  // Render: Rule form (create or edit)
  // -----------------------------------------------------------------------
  function renderForm() {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">
            {editingRule ? 'Edit Rule' : 'Create Automation Rule'}
          </h2>
          <button onClick={() => setMode('list')} className="text-sm text-slate-500 hover:text-slate-700">
            ← Back to list
          </button>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {/* Name & Description */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Rule Name</label>
            <input
              type="text"
              value={formName}
              onChange={e => setFormName(e.target.value)}
              placeholder="e.g., Payment Reminder (7 days)"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <input
              type="text"
              value={formDescription}
              onChange={e => setFormDescription(e.target.value)}
              placeholder="Optional description"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Trigger Event */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">When this happens (Trigger)</label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {TRIGGER_EVENTS.map(t => (
              <button
                key={t.value}
                onClick={() => setFormTrigger(t.value)}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-all ${
                  formTrigger === t.value
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-500'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <span>{t.icon}</span>
                <span className="truncate">{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Action Type */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Do this (Action)</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {ACTION_TYPES.map(a => (
              <button
                key={a.value}
                onClick={() => setFormAction(a.value)}
                className={`flex flex-col items-start rounded-lg border px-3 py-2.5 text-left transition-all ${
                  formAction === a.value
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-500'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span>{a.icon}</span>
                  <span className="font-medium text-sm">{a.label}</span>
                </div>
                <span className="text-xs text-slate-500 mt-0.5">{a.description}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Action-specific config */}
        {formAction === 'send_webhook' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Webhook URL</label>
            <input
              type="url"
              value={formWebhookUrl}
              onChange={e => setFormWebhookUrl(e.target.value)}
              placeholder="https://your-app.com/webhooks/billing"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        )}
        {formAction === 'send_email' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email Template Slug</label>
            <input
              type="text"
              value={formEmailTemplate}
              onChange={e => setFormEmailTemplate(e.target.value)}
              placeholder="e.g., payment-reminder"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        )}

        {/* Delay & Priority */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Delay (days)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                value={formDelay}
                onChange={e => setFormDelay(parseInt(e.target.value) || 0)}
                className="w-24 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
              <span className="text-sm text-slate-500">
                {formDelay === 0 ? 'Execute immediately' : `Wait ${formDelay} day${formDelay > 1 ? 's' : ''} before executing`}
              </span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
            <input
              type="number"
              min={0}
              value={formPriority}
              onChange={e => setFormPriority(parseInt(e.target.value) || 0)}
              className="w-24 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formActive}
                onChange={e => setFormActive(e.target.checked)}
                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-slate-700">Active</span>
            </label>
          </div>
        </div>

        {/* Plan associations */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Apply to Plans
            <span className="ml-2 text-xs font-normal text-slate-400">(leave empty = applies to all plans)</span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {allPlans.filter(p => p.status === 'active').map(plan => (
              <button
                key={plan.id}
                onClick={() => {
                  setFormPlanIds(prev =>
                    prev.includes(plan.id) ? prev.filter(id => id !== plan.id) : [...prev, plan.id]
                  );
                }}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-all ${
                  formPlanIds.includes(plan.id)
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-500'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${formPlanIds.includes(plan.id) ? 'bg-indigo-500' : 'bg-slate-300'}`} />
                <span className="truncate">{plan.name}</span>
              </button>
            ))}
          </div>
          {formPlanIds.length > 0 && (
            <p className="text-xs text-indigo-600 mt-1">
              Rule will only fire for events related to {formPlanIds.length} selected plan{formPlanIds.length > 1 ? 's' : ''}.
            </p>
          )}
        </div>

        {/* Save */}
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={() => void handleSave()}
            disabled={saving || !formName || !formTrigger || !formAction}
            className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
          </button>
          <button
            onClick={() => setMode('list')}
            className="rounded-lg border border-slate-300 px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render: Execution history
  // -----------------------------------------------------------------------
  function renderHistory() {
    const rule = rules.find(r => r.id === historyRuleId);
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">
            Execution History — {rule?.name ?? 'Unknown Rule'}
          </h2>
          <button onClick={() => setMode('list')} className="text-sm text-slate-500 hover:text-slate-700">
            ← Back to list
          </button>
        </div>

        {executions.length === 0 ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            No executions yet. This rule hasn't been triggered.
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium text-slate-600">Status</th>
                  <th className="px-4 py-2.5 text-left font-medium text-slate-600">Trigger</th>
                  <th className="px-4 py-2.5 text-left font-medium text-slate-600">Scheduled</th>
                  <th className="px-4 py-2.5 text-left font-medium text-slate-600">Executed</th>
                  <th className="px-4 py-2.5 text-left font-medium text-slate-600">Customer</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {executions.map(ex => (
                  <tr key={ex.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        ex.status === 'completed' ? 'bg-green-50 text-green-700' :
                        ex.status === 'failed' ? 'bg-red-50 text-red-700' :
                        ex.status === 'scheduled' ? 'bg-amber-50 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {ex.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-700">{ex.triggerEvent}</td>
                    <td className="px-4 py-2.5 text-slate-500">{new Date(ex.scheduledAt).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-slate-500">{ex.executedAt ? new Date(ex.executedAt).toLocaleString() : '—'}</td>
                    <td className="px-4 py-2.5 text-slate-500 font-mono text-xs">{ex.customerId ? ex.customerId.slice(0, 8) + '...' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render: Rule card
  // -----------------------------------------------------------------------
  function renderRuleCard(rule: AutomationRule) {
    return (
      <div key={rule.id} className={`rounded-lg border bg-white p-4 transition-all ${
        rule.isActive ? 'border-slate-200' : 'border-slate-200 opacity-60'
      }`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-slate-800 truncate">{rule.name}</h3>
              {!rule.isActive && (
                <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                  Paused
                </span>
              )}
            </div>
            {rule.description && (
              <p className="text-sm text-slate-500 mt-0.5 truncate">{rule.description}</p>
            )}
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-1.5 text-sm">
                <span>{triggerIcon(rule.triggerEvent)}</span>
                <span className="text-slate-600">{triggerLabel(rule.triggerEvent)}</span>
              </div>
              <span className="text-slate-300">→</span>
              {rule.delayDays > 0 && (
                <>
                  <span className="text-sm text-amber-600 font-medium">
                    {rule.delayDays}d delay
                  </span>
                  <span className="text-slate-300">→</span>
                </>
              )}
              <div className="flex items-center gap-1.5 text-sm">
                <span>{actionIcon(rule.actionType)}</span>
                <span className="text-slate-600">{actionLabel(rule.actionType)}</span>
              </div>
            </div>
            {/* Plan badges */}
            {(rulePlanMappings[rule.id] ?? []).length > 0 ? (
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                <span className="text-xs text-slate-400">Plans:</span>
                {(rulePlanMappings[rule.id] ?? []).map(pid => (
                  <span key={pid} className="inline-flex items-center rounded-full bg-indigo-50 border border-indigo-200 px-2 py-0.5 text-xs text-indigo-700">
                    {planName(pid)}
                  </span>
                ))}
              </div>
            ) : (
              <div className="mt-2">
                <span className="text-xs text-slate-400 italic">Applies to all plans</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => toggleMut.mutate({ id: rule.id, isActive: !rule.isActive })}
              className={`rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${
                rule.isActive
                  ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                  : 'bg-green-50 text-green-700 hover:bg-green-100'
              }`}
              title={rule.isActive ? 'Pause rule' : 'Activate rule'}
            >
              {rule.isActive ? 'Pause' : 'Activate'}
            </button>
            <button
              onClick={() => openEdit(rule)}
              className="rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
            >
              Edit
            </button>
            <button
              onClick={() => openHistory(rule.id)}
              className="rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
            >
              History
            </button>
            <button
              onClick={() => { if (confirm(`Delete rule "${rule.name}"?`)) deleteMut.mutate(rule.id); }}
              className="rounded-lg bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render: Main list
  // -----------------------------------------------------------------------
  function renderList() {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Automation Rules</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Configure what happens automatically when billing events occur.
            </p>
          </div>
          <button
            onClick={() => openCreate()}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            + New Rule
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-2xl font-bold text-slate-800">{rules.length}</div>
            <div className="text-sm text-slate-500">Total Rules</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-2xl font-bold text-green-600">{activeRules.length}</div>
            <div className="text-sm text-slate-500">Active</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-2xl font-bold text-slate-400">{inactiveRules.length}</div>
            <div className="text-sm text-slate-500">Paused</div>
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="text-center text-sm text-slate-500 py-8">Loading rules...</div>
        )}

        {/* Empty state */}
        {!isLoading && rules.length === 0 && (
          <div className="rounded-lg border-2 border-dashed border-slate-200 p-8 text-center">
            <div className="text-3xl mb-2">⚡</div>
            <h3 className="text-lg font-medium text-slate-700">No automation rules yet</h3>
            <p className="text-sm text-slate-500 mt-1 mb-4">
              Create rules to automatically provision access, send reminders, or notify external systems based on billing events.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <button
                onClick={() => openCreate()}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Create Custom Rule
              </button>
              <span className="text-sm text-slate-400 self-center">or use a template ↓</span>
            </div>
          </div>
        )}

        {/* Active rules */}
        {activeRules.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-2">Active Rules</h3>
            <div className="space-y-2">
              {activeRules.map(rule => renderRuleCard(rule))}
            </div>
          </div>
        )}

        {/* Paused rules */}
        {inactiveRules.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-2">Paused Rules</h3>
            <div className="space-y-2">
              {inactiveRules.map(rule => renderRuleCard(rule))}
            </div>
          </div>
        )}

        {/* Templates section */}
        <div className="border-t border-slate-200 pt-6">
          <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">Quick Start Templates</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {RULE_TEMPLATES.map((tpl, i) => (
              <button
                key={i}
                onClick={() => openCreate(tpl)}
                className="flex flex-col items-start rounded-lg border border-slate-200 bg-white p-4 text-left hover:border-indigo-300 hover:bg-indigo-50/30 transition-all group"
              >
                <div className="flex items-center gap-2">
                  <span>{triggerIcon(tpl.triggerEvent)}</span>
                  <span className="text-slate-300">→</span>
                  {tpl.delayDays > 0 && (
                    <>
                      <span className="text-xs text-amber-600 font-medium">{tpl.delayDays}d</span>
                      <span className="text-slate-300">→</span>
                    </>
                  )}
                  <span>{actionIcon(tpl.actionType)}</span>
                </div>
                <div className="font-medium text-sm text-slate-700 mt-2 group-hover:text-indigo-700">{tpl.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{tpl.description}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Main render
  // -----------------------------------------------------------------------
  return (
    <AdminLayout title="Automation Rules">
      <div className="max-w-5xl mx-auto">
        {mode === 'list' && renderList()}
        {(mode === 'create' || mode === 'edit') && renderForm()}
        {mode === 'history' && renderHistory()}
      </div>
    </AdminLayout>
  );
}
